import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

interface NavItemProps {
  href: string;
  icon: LucideIcon;
  labelKey: string;
  isCollapsed?: boolean;
}

export function NavItem({ href, icon: Icon, labelKey, isCollapsed = false }: NavItemProps) {
  const { t } = useTranslation('common');
  const location = useLocation();
  const isActive = location.pathname === href;
  const label = t(labelKey);

  const linkContent = (
    <Link
      to={href}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 text-[14px] relative min-w-0',
        isCollapsed && 'justify-center px-2',
        isActive
          ? 'bg-primary text-white font-medium shadow-sm'
          : 'text-sidebar-text hover:bg-sidebar-item-hover hover:text-sidebar-text-hover'
      )}
    >
      {/* Active indicator line */}
      {isActive && (
        <span className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary rounded-r-full -ml-3" />
      )}
      <Icon className="w-4 h-4 shrink-0" />
      <span
        className={cn(
          'transition-all duration-200',
          isCollapsed ? 'w-0 opacity-0 overflow-hidden' : 'flex-1 min-w-0 truncate'
        )}
      >
        {label}
      </span>
    </Link>
  );

  // Show tooltip only when collapsed
  if (isCollapsed) {
    return (
      <Tooltip delayDuration={0}>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" className="bg-gray-900 text-white border-gray-700">
          {label}
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkContent;
}
